/**
 * "Plans to read": the signed-in person's client plans waiting for their
 * acknowledgment, overdue first. Each opens the plan reader. Draws nothing
 * when there is nothing to read or the list is not offered to this person.
 */
import { useState } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import PlanReaderDialog from './PlanReaderDialog';
import { STATE_BADGE, STATE_LABEL, dueText, personName } from './planAcknowledgmentModel';

const SURFACE = {
  // The field-staff home card (FieldStaffHomeCards: explicit border style).
  card: 'rounded-xl border border-solid border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800',
  section: 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800',
};
const RULE = 'border-x-0 border-b-0 border-t border-solid border-gray-200 dark:border-gray-700';

/**
 * @param {object} props
 * @param {{ status: string, plans: object[], total: number, summary: object, reload: Function }} props.plans  useMyPlansToRead()
 * @param {'card'|'section'} [props.variant]
 * @param {string} [props.title]
 */
export default function PlansToReadList({ plans, variant = 'section', title = 'Plans to read' }) {
  const [reading, setReading] = useState(null);
  if (!plans || plans.status !== 'ok' || plans.plans.length === 0) return null;
  const overdue = plans.summary?.overdue || 0;
  return (
    <section aria-labelledby="plans-to-read-title" className={`${SURFACE[variant] || SURFACE.section} space-y-2`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpenIcon className="h-5 w-5 text-teal-700 dark:text-teal-400" aria-hidden="true" />
          <h2 id="plans-to-read-title" className="m-0 text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {overdue > 0 ? `${plans.total} to read, ${overdue} overdue` : `${plans.total} to read`}
        </span>
      </div>
      <p className="m-0 text-sm text-gray-600 dark:text-gray-400">A client&apos;s plan has a version you have not acknowledged yet. Read it, then confirm.</p>
      <ul className="m-0 list-none p-0">
        {plans.plans.map((plan, index) => (
          <li key={plan.documentId} className={`flex flex-wrap items-center justify-between gap-3 py-3 ${index > 0 ? RULE : ''}`}>
            <div className="min-w-0">
              <p className="m-0 font-semibold text-gray-900 dark:text-white">{personName(plan.patient)}</p>
              <p className="m-0 text-sm text-gray-600 dark:text-gray-400">{`${plan.title} · Version ${plan.version} · ${dueText(plan)}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[plan.state] || STATE_BADGE.pending}`}>{STATE_LABEL[plan.state] || plan.state}</span>
              <button type="button" onClick={() => setReading(plan.documentId)} className="min-h-10 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800">Read plan</button>
            </div>
          </li>
        ))}
      </ul>
      {plans.total > plans.plans.length && <p className="m-0 text-xs text-gray-600 dark:text-gray-400">{`Showing the first ${plans.plans.length} of ${plans.total}.`}</p>}
      {reading && <PlanReaderDialog documentId={reading} onClose={() => setReading(null)} onAcknowledged={() => plans.reload()} />}
    </section>
  );
}
